import { EventType } from '@lib/models/enums';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { PartKind, PartRenderer } from './types';
import { isAskUser } from './types';
import TextPart from './TextPart';
import ThoughtPart from './ThoughtPart';
import FunctionPart from './FunctionPart';
import WaitingPart from './WaitingPart';
import SuspendedPart from './SuspendedPart';
import BoxedPart from './BoxedPart';
import AskUserPart from './AskUserPart';
import UiToolDispatcher from './UiToolDispatcher';

export const DEFAULT_PART_RENDERERS: Record<PartKind, PartRenderer> = {
    text: TextPart,
    thought: ThoughtPart,
    function: FunctionPart,
    waiting: WaitingPart,
    suspended: SuspendedPart,
    boxed: BoxedPart,
    // The built-in dispatcher resolves the host's `uiTools` registry (name →
    // renderer/action) and draws the matched component, or falls back to a plain
    // tool call when there's no registry / no match. Hosts can still fully
    // override the kind via `partRenderers={{ uiTool }}` (MessagePart prefers it).
    uiTool: UiToolDispatcher,
    // AskUserPart widens props (only `part` required) so the pane-level
    // AskUserSheet can render it standalone; the registry always passes the
    // full PartRendererProps, so this cast is safe.
    askUser: AskUserPart as PartRenderer,
};

/**
 * Maps an `InteractionPart` to the kind of renderer that should handle it.
 * Returns `null` for parts that should not render anything (ServerError,
 * unknown/filtered event types like ToolEvent which is dropped upstream
 * in MessageAgent).
 */
export function classifyPart(part: InteractionPart): PartKind | null {
    switch (part.eventType) {
        case EventType.ServerError:
            return null;
        case EventType.Text:
            return 'text';
        case EventType.Thought:
            return 'thought';
        case EventType.Waiting:
            return 'waiting';
        case EventType.Suspended:
            return 'suspended';
        case EventType.UserAnswer:
            // A user turn's rich UI representation — carries functionDetails {name,args,isUi},
            // so the uiTool dispatcher resolves the host registry by name exactly like an
            // agent-emitted UI tool. Rendered on the user side (MessageUser); excluded from
            // the agent side (MessageAgent).
            return 'uiTool';
        case EventType.PartialFunctionCall:
        case EventType.FunctionCall:
        case EventType.FunctionResponse:
            // ask_user is a built-in interaction primitive — render it as an inline
            // yes/no prompt inside the library (not host-provided like uiTool).
            if (isAskUser(part.functionDetails?.name)) return 'askUser';
            // UI render tools (marked [KernelUiFunction] → functionDetails.isUi) are first-class
            // content — a distinct kind rendered by a host `uiTool` renderer, not gated by
            // showToolCalls. But render ONLY once the call has RESPONDED (status 'Done'): the tool's
            // render payload (functionDetails.clientOutput) and any ack/slot ride the RESULT, and
            // rendering on the bare call races multiple concurrent UI calls. Until the result folds
            // in, show it as an ordinary tool-call chip.
            if (part.functionDetails?.isUi && part.functionDetails?.status === 'Done') return 'uiTool';
            return 'function';
        case EventType.InlineData:
        case EventType.FileData:
        case EventType.CodeExecutionResult:
        case EventType.ExecutableCode:
        case EventType.Video:
        case EventType.Other:
        case EventType.NotDefined:
            return 'boxed';
        default:
            return null;
    }
}

export * from './types';
export * from './uiToolRegistry';
export { TextPart, ThoughtPart, FunctionPart, WaitingPart, SuspendedPart, BoxedPart };
export { default as UiToolDispatcher } from './UiToolDispatcher';
